import React, { useState, useRef, useEffect } from 'react';
import './PropertyCarousel.css';
import { parseISO } from 'date-fns';

// No longer need hardcoded logo function - using dynamic logoUrl from API

const PropertyCarousel = ({ currentPropertyIndex, setCurrentPropertyIndex, onPropertySelect, properties, checkinDate, checkoutDate, onShowCalendar }) => {
  // Format dates like iOS (e.g., "Dec 23 - Dec 27, 2024")
  const formatDateRange = (checkin, checkout) => {
    if (!checkin || !checkout) return '';
    const checkinDateObj = parseISO(checkin);
    const checkoutDateObj = parseISO(checkout);
    const formatOptions = { month: 'short', day: 'numeric', timeZone: 'UTC' };
    const checkinStr = checkinDateObj.toLocaleDateString('en-US', formatOptions);
    const checkoutStr = checkoutDateObj.toLocaleDateString('en-US', formatOptions);
    const year = checkoutDateObj.getFullYear();
    return `${checkinStr} - ${checkoutStr}, ${year}`;
  };
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const touchStartY = useRef(0);
  const touchEndY = useRef(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const videoRefs = useRef({});
  const [videosLoaded, setVideosLoaded] = useState({});
  const [videoErrors, setVideoErrors] = useState({});

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
    touchEndY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = () => {
    const swipeThreshold = 50;
    const diffX = touchStartX.current - touchEndX.current;
    const diffY = Math.abs(touchStartY.current - touchEndY.current);

    // Only trigger swipe if horizontal movement is greater than vertical
    if (Math.abs(diffX) > swipeThreshold && Math.abs(diffX) > diffY) {
      if (diffX > 0 && currentPropertyIndex < properties.length - 1) {
        navigateToProperty(currentPropertyIndex + 1);
      } else if (diffX < 0 && currentPropertyIndex > 0) {
        navigateToProperty(currentPropertyIndex - 1);
      }
    }
  };

  // Video management functions (following iOS logic)
  const pauseAllVideos = () => {
    Object.values(videoRefs.current).forEach(video => {
      if (video && !video.paused) {
        video.pause();
      }
    });
  };

  const playCurrentVideo = (index) => {
    const property = properties[index];
    if (property && property.hasVideo && property.backgroundOption === 'video') {
      const video = videoRefs.current[property.id];
      if (video && videosLoaded[property.id]) {
        video.currentTime = 0; // Reset to beginning like iOS
        video.play().catch(error => {
          console.log('Video autoplay prevented:', error);
          // Fallback: try to play without resetting time
          setTimeout(() => {
            video.play().catch(e => console.log('Video play failed:', e));
          }, 500);
        });
      }
    }
  };

  const handleVideoLoaded = (propertyId, index) => {
    if (!videosLoaded[propertyId]) {
      setVideosLoaded(prev => ({ ...prev, [propertyId]: true }));
      // Auto-play if this is the current property
      if (index === currentPropertyIndex) {
        setTimeout(() => playCurrentVideo(currentPropertyIndex), 100);
      }
    }
  };

  const handleVideoError = (propertyId, error) => {
    console.error(`Video failed to load for ${propertyId}:`, error);
    setVideoErrors(prev => ({ ...prev, [propertyId]: true }));
  };

  const navigateToProperty = (index) => {
    if (isTransitioning || index === currentPropertyIndex) return;

    // Pause current video (iOS behavior)
    pauseAllVideos();

    setIsTransitioning(true);
    setCurrentPropertyIndex(index);

    // Play new video after transition starts (iOS timing)
    setTimeout(() => {
      playCurrentVideo(index);
    }, 100);

    setTimeout(() => {
      setIsTransitioning(false);
    }, 600);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'ArrowLeft' && currentPropertyIndex > 0) {
      navigateToProperty(currentPropertyIndex - 1);
    } else if (e.key === 'ArrowRight' && currentPropertyIndex < properties.length - 1) {
      navigateToProperty(currentPropertyIndex + 1);
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentPropertyIndex]);

  // Handle initial video playback and property changes (iOS logic)
  useEffect(() => {
    if (properties.length > 0) {
      // Small delay to ensure video elements are rendered
      const timer = setTimeout(() => {
        pauseAllVideos();
        playCurrentVideo(currentPropertyIndex);
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [currentPropertyIndex, properties]);

  // Cleanup all videos on unmount
  useEffect(() => {
    return () => {
      pauseAllVideos();
    };
  }, []);

  const handlePropertySelect = (property) => {
    onPropertySelect(property);
  };

  // Return empty div if no properties
  if (!properties || properties.length === 0) {
    return <div className="property-carousel">No properties available</div>;
  }

  return (
    <div 
      className="property-carousel"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Date Display - matches iOS dateTextField */}
      <div 
        className="date-display"
        onClick={onShowCalendar}
      >
        {formatDateRange(checkinDate, checkoutDate)}
      </div>
      <div 
        className="property-container"
        style={{
          width: `${properties.length * 100}%`,
          transform: `translateX(-${currentPropertyIndex * (100 / properties.length)}%)`
        }}
      >
        {properties.map((property, index) => (
          <div 
            key={property.id} 
            className={`property-slide ${index === currentPropertyIndex ? 'active' : ''}`}
            onClick={() => {
              // Clicking a slide that is NOT active navigates to it
              if (index !== currentPropertyIndex) {
                navigateToProperty(index);
              }
              // If the current slide is tapped, do nothing – user must use "Book Now".
            }}
          >
            <div className="property-background">
              {property.hasVideo && property.backgroundOption === 'video' && !videoErrors[property.id] ? (
                <>
                  <video 
                    ref={el => videoRefs.current[property.id] = el}
                    className="property-video" 
                    src={property.videoUrl} 
                    loop 
                    muted 
                    playsInline
                    preload="metadata"
                    onLoadedData={() => handleVideoLoaded(property.id, index)}
                    onError={(e) => handleVideoError(property.id, e)}
                    onCanPlay={() => {
                      // Only play if this is the current property and video hasn't been played yet
                      if (index === currentPropertyIndex && !videosLoaded[property.id]) {
                        playCurrentVideo(currentPropertyIndex);
                      }
                    }}
                  />
                  {/* Fallback image while video loads */}
                  {!videosLoaded[property.id] && (
                    <img 
                      className="property-image property-fallback" 
                      src={property.imageUrl} 
                      alt={property.name}
                      style={{ position: 'absolute', top: 0, left: 0, zIndex: 1 }}
                    />
                  )}
                </>
              ) : (
                <img 
                  className="property-image" 
                  src={property.imageUrl} 
                  alt={property.name}
                />
              )}
              <div className="property-overlay" />
            </div>
            
            {/* Property Content - simplified without center logo */}
            <div className="property-content">
              {/* Property Title */}
              <h1 className="property-title">{property.name}</h1>
              
              {/* Book Now Button */}
              <button 
                className="book-now-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePropertySelect(property);
                }}
              >
                Book Now
              </button>
            </div>
          </div>
        ))}
      </div>
      
      {/* Navigation Arrows with elegant transparency */}
      <div className="property-nav-arrows">
        <button 
          className={`nav-arrow nav-arrow-left ${currentPropertyIndex === 0 ? 'disabled' : ''}`}
          onClick={() => navigateToProperty(currentPropertyIndex - 1)}
          disabled={currentPropertyIndex === 0 || isTransitioning}
        >
          ‹
        </button>
        <button 
          className={`nav-arrow nav-arrow-right ${currentPropertyIndex === properties.length - 1 ? 'disabled' : ''}`}
          onClick={() => navigateToProperty(currentPropertyIndex + 1)}
          disabled={currentPropertyIndex === properties.length - 1 || isTransitioning}
        >
          ›
        </button>
      </div>
    </div>
  );
};

export default PropertyCarousel;